/**
 * Glyph atlas builder. See CLAUDE.md architecture rule 3.
 *
 * Bakes a monospace font into an offscreen canvas → PixiJS Texture,
 * with one sub-Texture per glyph. White glyphs on transparent background
 * so callers can tint per-sprite.
 *
 * Two knobs kill atlas bleed (adjacent-cell pixels sampling into a glyph):
 *   - PAD of transparent pixels around every cell frame — even under linear
 *     filtering, edge samples land on transparent padding, not the neighbor
 *     glyph.
 *   - `scaleMode = 'nearest'` on the atlas texture source — no interpolation
 *     at all, which matches the DF/Qud pixel look and avoids soft edges at
 *     non-integer downscales.
 */

import { Rectangle, Texture } from 'pixi.js';

export interface GlyphAtlas {
  /** Width of an atlas cell in atlas pixels (inner content region). */
  cellW: number;
  /** Height of an atlas cell in atlas pixels (inner content region). */
  cellH: number;
  get(glyph: string): Texture | null;
}

/** Default glyph set. CP437-ish, covering what phases 0–5 will actually draw. */
const DEFAULT_GLYPHS = Array.from(
  new Set(
    (
      ' @#.+' +
      '=|/\\-_' +
      '░▒▓█' +
      '═║╔╗╚╝╠╣╦╩╬─│┌┐└┘├┤┬┴┼' +
      '↑↓←→' +
      'ハメ' + // player legs walk-cycle frames (katakana HA / ME)
      '♣♦♠♥' + // held-weapon suit glyphs (Main / Side / Melee / Heal)
      '▦☼✷⌂' + // fusebox panel + light fixtures (on / dim) + janitor closet
      '╥╤╨╧╪╫▤▥▧▨◊≣π§≡ロ≈†Θ∩⊙≤≥' + // furniture / prop + service-outlet + stairs (≣) + vent (ロ) glyphs
      'abcdefghijklmnopqrstuvwxyz' +
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
      '0123456789' +
      '!?*:;,\'"<>()[]{}'
    ).split(''),
  ),
).join('');

/** Transparent padding around each cell (atlas pixels each side). */
const PAD = 2;

export interface AtlasOptions {
  glyphs?: string;
  fontPx: number;
  fontFamily: string;
  /** Cells per atlas row. Purely a packing choice. */
  cols?: number;
}

export function buildAtlas(opts: AtlasOptions): GlyphAtlas {
  const glyphList = Array.from(new Set(opts.glyphs ?? DEFAULT_GLYPHS));
  const fontPx = opts.fontPx;
  const cols = opts.cols ?? 16;

  const meas = document.createElement('canvas').getContext('2d');
  if (!meas) throw new Error('atlas: no 2d canvas context');
  meas.font = `${fontPx}px ${opts.fontFamily}`;

  // Widest glyph in the set — monospace fonts should return the same for any
  // ASCII char, but box-drawing Unicode glyphs can be slightly wider.
  let maxW = 0;
  for (const ch of glyphList) maxW = Math.max(maxW, meas.measureText(ch).width);
  const cellW = Math.ceil(maxW);
  const cellH = Math.ceil(fontPx * 1.15);

  // Actual grid stride includes padding on both sides of every cell.
  const stride = { x: cellW + PAD * 2, y: cellH + PAD * 2 };

  const rows = Math.ceil(glyphList.length / cols);
  const canvas = document.createElement('canvas');
  canvas.width = stride.x * cols;
  canvas.height = stride.y * rows;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('atlas: no 2d canvas context');
  ctx.font = `${fontPx}px ${opts.fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';

  const rects = new Map<string, Rectangle>();
  glyphList.forEach((ch, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    // Inner cell origin (skipping padding).
    const x = col * stride.x + PAD;
    const y = row * stride.y + PAD;
    ctx.fillText(ch, x + cellW / 2, y + cellH / 2);
    rects.set(ch, new Rectangle(x, y, cellW, cellH));
  });

  const base = Texture.from(canvas);
  // Nearest-neighbor sampling for a crisp pixel look and to avoid any
  // remaining subpixel bleed at fractional downscales.
  base.source.scaleMode = 'nearest';

  const map = new Map<string, Texture>();
  for (const [ch, r] of rects) {
    map.set(ch, new Texture({ source: base.source, frame: r }));
  }

  return {
    cellW,
    cellH,
    get(g) {
      return map.get(g) ?? null;
    },
  };
}
